import { Toggle } from '@cimpress/react-components';
import IconAlertTriangle from '@cimpress-technology/react-streamline-icons/lib/IconAlertTriangle';
import IconRemoveCircle from '@cimpress-technology/react-streamline-icons/lib/IconRemoveCircle';
import IconInformationCircle from '@cimpress-technology/react-streamline-icons/lib/IconInformationCircle';
import { equals, reverse } from 'ramda';
import React, { useEffect, useRef, useState } from 'react';
// tslint:disable-next-line:import-name
import Inspector from 'react-json-inspector';
import { useInterval, useScroll } from 'react-use';
import { useWorkflowLog } from '../../../adapters/use-workflow-log';
import { UseWorkflowLogHook, WorkflowLogEntry as WorkflowLogEntryModel } from '../../../ports/WorkflowService';
import { colors } from '../colors';
import { AlignItems, FlexBox, FlexDirection, JustifyContent } from '../../../utils/css-classes';
import { LocaleSettings, Location } from '../../../models/location';
import momentTimezone from 'moment-timezone';
import { useCurrentLocation } from '../../../store/hooks/useCurrentLocation';

// tslint:disable-next-line:import-name
import style from './index.module.css';

export interface WorkflowLogProps {
  workflowId: string;
  isRefreshing: boolean;
}

// tslint:disable-next-line:function-name
function WorkflowLogEntry(
  {
    entry,
    localeSettings,
  }: {
    entry: WorkflowLogEntryModel;
    localeSettings: LocaleSettings;
  },
) {
  return <>
    <div className={`${FlexBox.FLEX} ${FlexDirection.ROW}`}>
      <p>
        {momentTimezone(entry.timestamp).tz(localeSettings.timezone).format('HH:mm:ss.SSS')}
      </p>
      <div className={style.Level}>
        {entry.level === 'error' && <IconRemoveCircle color={`var(--${colors['error']})`} />}
        {entry.level === 'warn' && <IconAlertTriangle color={`var(--${colors['warn']})`} />}
        {entry.level === 'info' && <IconInformationCircle color={`var(--${colors['info']})`} />}
      </div>
      <div>
        <p>{entry.message}</p>
        {!equals(entry.payload, {}) && <Inspector data={entry.payload} search={false} />}
      </div>
    </div>
  </>;
}

export function createWorkflowLog(
  {
    useCurrentLocation,
    useWorkflowLog,
  }: {
    useCurrentLocation: () => Location;
    useWorkflowLog: UseWorkflowLogHook;
  },
) {
  return function WorkflowLog(
    {
      workflowId,
      isRefreshing,
    }: WorkflowLogProps,
  ) {
    const { localeSettings } = useCurrentLocation();
    const {
      log,
      update,
    } = useWorkflowLog(workflowId);
    const [isAutoScrolling, setAutoScrolling] = useState(true);
    const logDiv = useRef<HTMLDivElement>(null);
    const { y } = useScroll(logDiv);
    const [oldY, setOldY] = useState(0);
    const bottomAnchor = useRef<HTMLDivElement>(null);

    useInterval(update, isRefreshing ? 1500 : null);
    useEffect(
      () => {
        if (isAutoScrolling) {
          bottomAnchor.current?.scrollIntoView();
        }
      },
      [bottomAnchor, log, isAutoScrolling],
    );

    useEffect(
      () => {
        if (y < oldY) {
          setAutoScrolling(false);
        }
        setOldY(y);
      },
      [y],
    );

    const classes = [
      style.AutoScroll,
      FlexBox.FLEX,
      FlexDirection.ROW,
      JustifyContent.FLEX_END,
      AlignItems.CENTER,
    ].join(' ');
    return <>
      <div className={classes}>
        <h6>
          Auto-scroll&nbsp;
        </h6>
        <Toggle on={isAutoScrolling} onClick={() => setAutoScrolling(!isAutoScrolling)} size="sm" />
      </div>
      <div className={`${style.Log}`} ref={logDiv}>
        {/* !!log && totalCount !== log.length && <Button onClick={loadMore}>Load more entries.</Button> */}
        {!!log && reverse(log).map(
          entry => <WorkflowLogEntry key={entry.id} entry={entry} localeSettings={localeSettings} />,
        )}
        <div ref={bottomAnchor} />
      </div>
    </>;
  };
}

// tslint:disable-next-line:variable-name
export const WorkflowLog = createWorkflowLog({ useWorkflowLog, useCurrentLocation });
